import { useState, useEffect } from "react";
import AttendancePageSwapper from "../../../components/Attendance/AttendancePageSwapper";
import AttendanceAdminStudentsTable, { AdminStudent } from "../../../components/Attendance/AttendanceAdminStudentsTable";
import AttendanceAdminLessonsList, { AdminLesson } from "../../../components/Attendance/AttendanceAdminLessonsList";
import AttendanceWeeklySchedule from "../../../components/Attendance/AttendanceWeeklySchedule";
import AddStudentModal from "../../../components/Attendance/AddStudentModal";
import CreateLessonModal, { LessonFormData } from "../../../components/Attendance/CreateLessonModal";
import AddCourseModal from "../../../components/Attendance/AddCourseModal";

// Mock данные - должны приходить с API
const mockStudents: AdminStudent[] = [
    {
        id: 1,
        student: {
            id: 1,
            external_user_id: 1,
            profile_image: null,
            json_saved_data: {
                id: 1,
                role: 'student',
                email: 'aruzhan@example.com',
                mobile: '',
                lastname: 'Барушева',
                firstname: 'Аружан',
                department: '',
                externalId: '123456789',
                yearOfStudy: null,
                organization: '',
                organizationId: 0,
                subOrganization: '',
                subOrganizationId: 0
            }
        },
        studentId: '123456789',
        className: 'Радиофизика 1 курс',
        totalScore: 26
    },
    {
        id: 2,
        student: {
            id: 2,
            external_user_id: 2,
            profile_image: null,
            json_saved_data: {
                id: 2,
                role: 'student',
                email: 'amanay@example.com',
                mobile: '',
                lastname: 'Балат',
                firstname: 'Аманай',
                department: '',
                externalId: '345678912',
                yearOfStudy: null,
                organization: '',
                organizationId: 0,
                subOrganization: '',
                subOrganizationId: 0
            }
        },
        studentId: '345678912',
        className: 'Радиофизика 1 курс',
        totalScore: 32
    },
    {
        id: 3,
        student: {
            id: 3,
            external_user_id: 3,
            profile_image: null,
            json_saved_data: {
                id: 3,
                role: 'student',
                email: 'danila@example.com',
                mobile: '',
                lastname: 'Дементьев',
                firstname: 'Данила',
                department: '',
                externalId: '567891234',
                yearOfStudy: null,
                organization: '',
                organizationId: 0,
                subOrganization: '',
                subOrganizationId: 0
            }
        },
        studentId: '567891234',
        className: 'Радиофизика 1 курс',
        totalScore: 27
    },
];

// Mock данные для уроков по курсам
const mockLessonsByCourse: Record<string, AdminLesson[]> = {
    'Introduction to Management': [
        { id: 11, date: "10.08.2024", averageScore: 4.9, status: 'completed' },
        { id: 12, date: "09.08.2024", averageScore: 4.8, status: 'completed' },
        { id: 13, date: "08.08.2024", averageScore: 4.7, status: 'in_progress' },
        { id: 14, date: "07.08.2024", averageScore: 4.6, status: 'upcoming' },
    ],
    'ICT': [
        { id: 21, date: "15.08.2024", averageScore: 4.5, status: 'completed' },
        { id: 22, date: "14.08.2024", averageScore: 4.7, status: 'completed' },
        { id: 23, date: "13.08.2024", averageScore: 4.8, status: 'in_progress' },
        { id: 24, date: "12.08.2024", averageScore: 4.6, status: 'upcoming' },
        { id: 25, date: "11.08.2024", averageScore: 4.9, status: 'upcoming' },
    ],
};

const courses = ['Расписание всех предметов на неделю', ...Object.keys(mockLessonsByCourse)];

export default function AttendanceAdminPage() {
    const [selectedPage, setSelectedPage] = useState<string>(courses[0]);
    const [students, setStudents] = useState<AdminStudent[]>(mockStudents);
    const [lessons, setLessons] = useState<Record<string, AdminLesson[]>>(mockLessonsByCourse);
    const [courseList, setCourseList] = useState<string[]>(courses);
    const [selectedLesson, setSelectedLesson] = useState<AdminLesson | undefined>(undefined);
    const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
    const [isCreateLessonModalOpen, setIsCreateLessonModalOpen] = useState(false);
    const [isAddCourseModalOpen, setIsAddCourseModalOpen] = useState(false);

    // Получаем уроки для выбранного курса
    const currentLessons = lessons[selectedPage] || [];

    // Проверяем, выбрана ли вкладка "Расписание всех предметов на неделю"
    const isWeeklyScheduleView = selectedPage === 'Расписание всех предметов на неделю';

    // Сброс выбранного урока при смене курса
    useEffect(() => {
        setSelectedLesson(undefined);
    }, [selectedPage]);

    const handleAddStudent = (studentData: { email: string; className?: string }) => {
        // TODO: Отправить данные на бэкэнд когда будет готов API
        console.log("Adding student:", studentData);
        
        // Симуляция добавления студента
        const newStudent: AdminStudent = {
            id: Math.max(...students.map(s => s.id), 0) + 1,
            student: {
                id: students.length + 1,
                external_user_id: students.length + 1,
                profile_image: null,
                json_saved_data: {
                    id: students.length + 1,
                    role: 'student',
                    email: studentData.email,
                    mobile: '',
                    lastname: 'Новый',
                    firstname: 'Студент',
                    department: '',
                    externalId: `${Date.now()}`,
                    yearOfStudy: null,
                    organization: '',
                    organizationId: 0,
                    subOrganization: '',
                    subOrganizationId: 0
                }
            },
            studentId: `${Date.now()}`,
            className: studentData.className || 'Не указан',
            totalScore: 0
        };
        
        setStudents(prev => [...prev, newStudent]);
        
        // В реальном приложении здесь будет вызов API:
        // await studentsApi.addToCourse({ email: studentData.email, className: studentData.className });
    };

    const handleDeleteStudent = (studentId: number) => {
        // TODO: Отправить запрос на удаление на бэкэнд
        console.log("Deleting student:", studentId);
        setStudents(prev => prev.filter(s => s.id !== studentId));
        
        // В реальном приложении здесь будет вызов API:
        // await studentsApi.removeFromCourse(studentId);
    };

    const handleSubmitLesson = (lessonData: LessonFormData) => {
        // TODO: Отправить данные на бэкэнд когда будет готов API
        console.log("Creating lesson with data:", lessonData);
        
        const newLessonId = Math.max(...Object.values(lessons).flat().map(l => l.id), 0) + 1;
        const formattedDate = lessonData.date 
            ? lessonData.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : new Date().toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
        
        const newLesson: AdminLesson = {
            id: newLessonId,
            date: formattedDate,
            averageScore: 0,
            status: 'upcoming'
        };
        
        setLessons(prev => ({
            ...prev,
            [selectedPage]: [...(prev[selectedPage] || []), newLesson]
        }));
    };

    const handleStartAttendance = (lessonId: number) => {
        // TODO: Отправить запрос на начало аттенданса на бэкэнд
        console.log("Starting attendance for lesson:", lessonId);
        setLessons(prev => ({
            ...prev,
            [selectedPage]: (prev[selectedPage] || []).map(lesson => 
                lesson.id === lessonId ? { ...lesson, status: 'in_progress' as const } : lesson
            )
        }));
        
        // В реальном приложении здесь будет вызов API:
        // await attendanceApi.start(lessonId);
    };

    const handleStopAttendance = (lessonId: number) => {
        // TODO: Отправить запрос на остановку аттенданса на бэкэнд
        console.log("Stopping attendance for lesson:", lessonId);
        setLessons(prev => ({
            ...prev,
            [selectedPage]: (prev[selectedPage] || []).map(lesson => 
                lesson.id === lessonId ? { ...lesson, status: 'completed' as const } : lesson
            )
        }));
        
        // В реальном приложении здесь будет вызов API:
        // await attendanceApi.stop(lessonId);
    };

    const handleStudentList = (lessonId: number) => {
        // TODO: Показать список студентов для урока
        console.log("Showing student list for lesson:", lessonId);
        // Можно открыть модалку или перейти на другую страницу
    };

    const handleDeleteLesson = (lessonId: number) => {
        // TODO: Отправить запрос на удаление урока на бэкэнд
        console.log("Deleting lesson:", lessonId);
        setLessons(prev => ({
            ...prev,
            [selectedPage]: (prev[selectedPage] || []).filter(l => l.id !== lessonId)
        }));
        
        // В реальном приложении здесь будет вызов API:
        // await lessonsApi.delete(lessonId);
    };

    const handleAddCourse = (courseName: string) => {
        // TODO: Отправить данные на бэкэнд когда будет готов API
        console.log("Adding course:", courseName);
        
        // Добавляем курс в список (после "Расписание всех предметов на неделю")
        setCourseList(prev => {
            const weeklySchedule = 'Расписание всех предметов на неделю';
            const otherCourses = prev.filter(c => c !== weeklySchedule);
            return [weeklySchedule, ...otherCourses, courseName];
        });
        
        // Инициализируем пустой массив уроков для нового курса
        setLessons(prev => ({
            ...prev,
            [courseName]: []
        }));
        
        // Переключаемся на новый курс
        setSelectedPage(courseName);
        
        // В реальном приложении здесь будет вызов API:
        // await coursesApi.create({ name: courseName });
    };

    const handleEditLesson = (lessonId: number) => {
        // TODO: Реализовать редактирование урока
        console.log("Editing lesson:", lessonId);
    };

    const handleUploadList = () => {
        // TODO: Реализовать загрузку списка студентов
        console.log("Uploading student list");
    };

    const handleDownloadList = () => {
        // TODO: Реализовать выгрузку списка студентов
        console.log("Downloading student list");
    };

    const handleSearchCourse = () => {
        // TODO: Реализовать поиск на курсе
        console.log("Searching on course");
    };

    return (
        <div className="bg-[#f8f9fd] h-full text-black p-4 md:p-5 text-sm overflow-y-auto">
            <div className="mb-4">
                <AttendancePageSwapper 
                    pages={courseList} 
                    selectedPage={selectedPage} 
                    setSelectedPage={setSelectedPage}
                    onAddCourse={() => setIsAddCourseModalOpen(true)}
                />
            </div>
            <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-160px)] md:h-[calc(100vh-180px)] min-h-0">
                {isWeeklyScheduleView ? (
                    // Показываем недельное расписание для вкладки "Расписание всех предметов на неделю"
                    <div className="w-full flex-1 min-h-0">
                        <AttendanceWeeklySchedule lessons={[]} />
                    </div>
                ) : (
                    <>
                        {/* Левая часть - Студенты на курсе */}
                        <div className="w-full lg:w-2/3 min-w-0 flex-shrink-0 flex flex-col min-h-0">
                            <AttendanceAdminStudentsTable
                                students={students}
                                onAddStudent={() => setIsAddStudentModalOpen(true)}
                                onDeleteStudent={handleDeleteStudent}
                                onUploadList={handleUploadList}
                                onDownloadList={handleDownloadList}
                                onSearchCourse={handleSearchCourse}
                            />
                        </div>

                        {/* Правая часть - Посещаемость курса */}
                        <div className="w-full lg:w-1/3 lg:min-w-[350px] flex-shrink-0 flex flex-col min-h-0">
                            <AttendanceAdminLessonsList
                                lessons={currentLessons}
                                selectedLesson={selectedLesson}
                                onLessonSelect={setSelectedLesson}
                                onCreateLesson={() => setIsCreateLessonModalOpen(true)}
                                onStartAttendance={handleStartAttendance}
                                onStopAttendance={handleStopAttendance}
                                onStudentList={handleStudentList}
                                onDeleteLesson={handleDeleteLesson}
                                onEditLesson={handleEditLesson}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Модалка добавления студента */}
            <AddStudentModal
                isOpen={isAddStudentModalOpen}
                onClose={() => setIsAddStudentModalOpen(false)}
                onSubmit={handleAddStudent}
            />

            {/* Модалка создания урока */}
            <CreateLessonModal
                isOpen={isCreateLessonModalOpen}
                onClose={() => setIsCreateLessonModalOpen(false)}
                onSubmit={handleSubmitLesson}
            />

            {/* Модалка добавления курса */}
            <AddCourseModal
                isOpen={isAddCourseModalOpen}
                onClose={() => setIsAddCourseModalOpen(false)}
                onSubmit={handleAddCourse}
            />
        </div>
    );
}

